// The Worklist's Appointment date is the pickup date (item 8, 2026-10-28): one value, not two. The
// column shows the date of the pickup a line is on (or shipped on); entering a date schedules the
// line — Schedule pickup with that date — and changing it reschedules, clearing it takes the line
// off its pickup. A date stored on a line before (typed, or a pre-route's) that is on no pickup is
// "not scheduled": shown muted, never scheduled by itself; entering it again schedules the line.

import type { LineView } from "./assemble";

/** What the Appointment date cell shows. */
export interface AppointmentCell {
  /** The pickup's date (YYYY-MM-DD), or "". */
  value: string;
  /** A stored date on a line on no pickup: shown muted, "Not on a pickup yet". */
  notScheduled: string;
  /** Shipped, or on a shipped pickup: nothing to change. */
  readOnly: boolean;
}

type Line = Pick<LineView, "pickupDate" | "fields" | "finished" | "onPickup">;

export function appointmentCell(line: Line): AppointmentCell {
  const value = line.pickupDate ?? "";
  return {
    value,
    notScheduled: !value && !line.finished ? line.fields.appointmentDate : "",
    readOnly: !!line.finished,
  };
}

/** A stored appointment date the line is not scheduled for: the "Appointment not scheduled" chip. */
export const appointmentNotScheduled = (line: Line) => !!appointmentCell(line).notScheduled;

/** What entering (or clearing) the date on these lines does. */
export type AppointmentPlan<L> =
  | { kind: "schedule"; lines: L[]; date: string }
  | { kind: "reschedule"; lines: L[]; date: string }
  | { kind: "clear"; takeOff: L[]; forget: L[] }
  | { kind: "none" }
  | { kind: "refuse"; title: string; why: string };

/**
 * A date typed on one line, or on the selected lines (`typed`: YYYY-MM-DD, or "" to clear):
 * - on no pickup: Schedule pickup on that date (the dialog; Cancel saves nothing);
 * - on a pickup: Reschedule to that date (the same date: nothing);
 * - cleared: off its pickup (asked first); a stored date not scheduled is just forgotten;
 * - shipped: refused; some on a pickup and some not: refused — each is its own action.
 */
export function appointmentPlan<L extends Line>(lines: readonly L[], typed: string): AppointmentPlan<L> {
  if (lines.some((l) => appointmentCell(l).readOnly))
    return {
      kind: "refuse",
      title: "Can't change the appointment date",
      why: "A shipped line keeps the date of the pickup it shipped on.",
    };
  const on = lines.filter((l) => l.onPickup);
  const off = lines.filter((l) => !l.onPickup);
  if (!typed) {
    const forget = off.filter((l) => l.fields.appointmentDate);
    return on.length || forget.length ? { kind: "clear", takeOff: on, forget } : { kind: "none" };
  }
  if (on.length && off.length)
    return {
      kind: "refuse",
      title: "Schedule these separately",
      why: "Some of these lines are on a pickup and some aren't: select those on a pickup to reschedule them, and the others to schedule them.",
    };
  if (off.length) return { kind: "schedule", lines: off, date: typed };
  return on.every((l) => l.pickupDate === typed) ? { kind: "none" } : { kind: "reschedule", lines: on, date: typed };
}
